'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useRouter } from '@/i18n/navigation';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { IoChevronDown, IoArrowRedoOutline } from 'react-icons/io5';
import { AvailabilityCalendar } from './availability-calendar';
import { BookingSummary } from './booking-summary';
import { useBookingStore } from '@/stores/booking-store';
import { addOneDay } from '@/lib/calendar-utils';
import type { CalendarResponse, CalendarDay, DateInterval } from '@/types/calendar';

interface RoomHeroProps {
  name: string;
  roomTypeName?: string;
  roomNumber?: string;
  mainImageUrl?: string | null;
  /**
   * Vertical focal point for the background crop (0 = top, 100 = bottom).
   * Per-room because `bg-cover` over a viewport-shaped box otherwise clips the
   * subject (e.g. bed) when the source image is portrait. Defaults to 50.
   */
  heroFocalY?: number;
  roomSlug: string;
  initialCalendarData: CalendarResponse;
  bookingDisabled?: boolean;
}

// Calculate minimum price from calendar data. Prefer the server-emitted
// `minPrice` (already includes the next-month fallback); fall back to a
// non-past day price across the visible month — dropping the `available`
// filter is intentional because PYN's rate-card price is independent of
// per-day availability, so a fully booked month still has an honest
// "starting from" figure.
function calculateMinPrice(response: CalendarResponse): number | null {
  if (!response.success || !response.data) return null;
  if (response.data.minPrice !== null && response.data.minPrice !== undefined) {
    return response.data.minPrice;
  }
  const prices = response.data.days
    .filter((day) => !day.isPast && day.price !== null)
    .map((day) => day.price as number);
  return prices.length > 0 ? Math.min(...prices) : null;
}

export function RoomHero({
  name,
  roomTypeName,
  roomNumber,
  mainImageUrl,
  heroFocalY = 50,
  roomSlug,
  initialCalendarData,
  bookingDisabled = false,
}: RoomHeroProps) {
  const router = useRouter();
  const t = useTranslations('roomDetail.hero');
  const { items, setDates, addRoom, toggleRoom, setSingleRoomCheckout, singleRoomCheckout } = useBookingStore();
  const isSelected = items.some((i) => i.roomSlug === roomSlug);
  const hasSelection = items.length > 0;
  // The selection bar is only visible for multi-room flows (not single-room checkout)
  const selectionBarVisible = hasSelection && !singleRoomCheckout;
  const [isHeartLiked, setIsHeartLiked] = useState(false);
  const isSharingRef = useRef(false);

  // Date interval selection state — read from URL params on mount
  const [interval, setInterval] = useState<DateInterval>({ checkIn: null, checkOut: null });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkIn = params.get('checkIn');
    const checkOut = params.get('checkOut');
    if (checkIn && checkOut) {
      setInterval({ checkIn, checkOut });
    } else if (params.get('intent') === 'book') {
      // Arrived with booking intent but no dates — auto-focus calendar
      setTimeout(() => {
        calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setCalendarHighlight(true);
        setShowSelectDatesMsg(true);
      }, 800);
    } else {
      // Fallback: restore dates from store (e.g., after back-navigation from checkout)
      const { checkIn: storeCheckIn, checkOut: storeCheckOut } = useBookingStore.getState();
      if (storeCheckIn && storeCheckOut) {
        setInterval({ checkIn: storeCheckIn, checkOut: storeCheckOut });
      }
    }
  }, []);

  // All calendar days for price calculation (includes data from navigated months)
  const [allCalendarDays, setAllCalendarDays] = useState<CalendarDay[]>(
    () => initialCalendarData.data?.days || []
  );

  const [minPrice, setMinPrice] = useState<number | null>(() =>
    calculateMinPrice(initialCalendarData)
  );
  const [currency] = useState(initialCalendarData.data?.currency || 'EUR');

  // Track if calendar should be shown - hide on SSR failure, runtime errors, or when booking is disabled
  const [showCalendar, setShowCalendar] = useState(
    () => !bookingDisabled && initialCalendarData.success === true
  );

  // Callback for calendar errors during month navigation
  const handleCalendarError = () => {
    setShowCalendar(false);
  };

  // Calendar highlight ref for the "Book Now" CTA scroll-to-calendar feature
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarHighlight, setCalendarHighlight] = useState(false);
  const [showSelectDatesMsg, setShowSelectDatesMsg] = useState(false);

  // Handle interval change from calendar — also dismisses select-dates message
  const handleIntervalChange = useCallback((newInterval: DateInterval) => {
    setInterval(newInterval);
    if (showSelectDatesMsg) {
      setShowSelectDatesMsg(false);
      setCalendarHighlight(false);
    }
  }, [showSelectDatesMsg]);

  const roomData: import('@/stores/booking-store').SelectionItem = {
    roomSlug,
    roomName: name,
    roomImageUrl: mainImageUrl || null,
    roomTypeName: roomTypeName || null,
  };

  // Handle "Book Now" — proceed to checkout, assuming 1 night if only check-in selected
  // Additive: if selection exists, adds this room to it; otherwise single-room quick path
  const handleBookNow = useCallback(() => {
    const checkIn = interval.checkIn;
    const checkOut = interval.checkOut;

    if (hasSelection) {
      // Additive: add this room to existing multi-room selection, go to checkout
      addRoom(roomData);
      router.push('/checkout');
    } else if (checkIn) {
      // Single-room quick path: set dates + add room, flag as single-room checkout
      const finalCheckOut = checkOut || addOneDay(checkIn);
      setSingleRoomCheckout(true);
      setDates(checkIn, finalCheckOut);
      addRoom(roomData);
      router.push('/checkout');
    } else {
      // No dates at all → highlight calendar (persistent until dates picked)
      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCalendarHighlight(true);
      setShowSelectDatesMsg(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, hasSelection, roomSlug, name, mainImageUrl, roomTypeName, router]);

  // Handle "Select this" — toggle room in/out of selection
  const handleSelectThis = useCallback(() => {
    const checkIn = interval.checkIn;
    const checkOut = interval.checkOut;

    if (checkIn) {
      const finalCheckOut = checkOut || addOneDay(checkIn);
      // Ensure shared dates are set (first selection sets them)
      if (!hasSelection) {
        setDates(checkIn, finalCheckOut);
      }
      // Explicit multi-room action — clear single-room flag if set
      setSingleRoomCheckout(false);
      toggleRoom(roomData);
    } else {
      // No dates → highlight calendar (persistent until dates picked)
      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCalendarHighlight(true);
      setShowSelectDatesMsg(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, hasSelection, roomSlug, name, mainImageUrl, roomTypeName]);

  // Handle calendar data change (when navigating months)
  const handleCalendarDataChange = useCallback((days: CalendarDay[]) => {
    setAllCalendarDays((prev) => {
      // Merge new days with existing, avoiding duplicates
      const existingDates = new Set(prev.map((d) => d.date));
      const newDays = days.filter((d) => !existingDates.has(d.date));
      return [...prev, ...newDays];
    });
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setInterval({ checkIn: null, checkOut: null });
  }, []);

  // Handle prices loaded from month navigation - always show current month's price
  const handlePricesLoaded = (newMinPrice: number | null) => {
    setMinPrice(newMinPrice);
  };

  useEffect(() => {
    const handleHighlight = () => {
      if (!hasCompleteInterval) {
        setCalendarHighlight(true);
        setShowSelectDatesMsg(true);
      }
    };
    window.addEventListener('highlight-calendar', handleHighlight);
    return () => window.removeEventListener('highlight-calendar', handleHighlight);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval.checkIn, interval.checkOut]);

  // Determine if we have a complete interval
  const hasCompleteInterval = interval.checkIn && interval.checkOut;

  // Format price with currency symbol
  const formatPrice = (price: number | null): string => {
    if (price === null) return '---';
    return currency === 'EUR' ? `€${Math.round(price)}` : `${Math.round(price)} ${currency}`;
  };

  const handleHeartClick = () => {
    setIsHeartLiked(!isHeartLiked);
  };

  // Copy the room URL as the fallback path. `navigator.clipboard` is absent on
  // insecure origins, so a missing `writeText` must surface as a failure rather
  // than resolving silently into a false "copied" toast.
  const copyRoomLink = async (url: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable');
      await navigator.clipboard.writeText(url);
      toast.success(t('shareCopied'));
    } catch {
      toast.error(t('shareFailed'));
    }
  };

  // Prefer the native share sheet (mobile + Safari); fall back to the clipboard
  // elsewhere. The sheet owns its own cancel affordance, so an AbortError is a
  // deliberate user action — never an error toast. The ref guards against a
  // second tap while a sheet is open (Web Share throws InvalidStateError).
  const handleShareClick = async () => {
    if (isSharingRef.current) return;
    const url = window.location.href;
    const shareData = { title: name, text: t('shareText', { name }), url };

    isSharingRef.current = true;
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
      } else {
        await copyRoomLink(url);
      }
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        await copyRoomLink(url);
      }
    } finally {
      isSharingRef.current = false;
    }
  };

  return (
    <>
      <section className="relative w-full min-h-screen">
        {/* Background image — shorter on mobile to avoid wasting space behind sticky bar */}
        <div
          className="absolute top-0 left-0 right-0 bottom-14 md:bottom-0 bg-cover"
          style={{
            backgroundImage: mainImageUrl
              ? `url(${mainImageUrl})`
              : undefined,
            // Y axis is set via inline style (per-room focal point); the
            // `slowHorizontalMove` keyframes only animate the X axis so this
            // value is preserved throughout the pan.
            backgroundPositionY: `${heroFocalY}%`,
            animation: mainImageUrl ? 'slowHorizontalMove 80s ease-in-out infinite' : undefined,
          }}
        >
          {!mainImageUrl && (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-950 via-stone-900 to-orange-950" />
          )}
        </div>

        {/* Mobile gradient overlay */}
        <div
          className="absolute inset-0 md:hidden pointer-events-none z-[1]"
          style={{
            background: 'linear-gradient(to bottom, transparent 59%, rgba(0,0,0,0.3) 86%, rgba(0,0,0,0.3) 100%)',
          }}
        />

        {/* Content container */}
        <div className="absolute inset-0 flex flex-col justify-between px-5 pt-5 pb-20 md:p-10 md:pt-[120px] lg:p-14 lg:pt-[120px] z-[2]">
          {/* Top section - title and actions */}
          <div className="flex flex-col gap-3">
            {/* Title — full width on mobile with right padding to clear hamburger */}
            <h1
              className="text-[7.5vw] md:text-6xl lg:text-7xl xl:text-8xl font-semibold text-white font-poppins leading-none self-start backdrop-blur-[1px] pr-14 md:pr-0"
              style={{ textShadow: '7px 3px 18px black' }}
            >
              {name}
            </h1>
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
              {(roomTypeName || roomNumber) && (
                <span
                  className="text-[5.2vw] md:text-2xl lg:text-3xl font-medium text-white font-poppins px-1.5 rounded-lg w-max backdrop-blur-[1px]"
                  style={{ textShadow: '1px 1px 6px black' }}
                >
                  {roomTypeName}{roomTypeName && roomNumber && ' / '}{roomNumber && t('roomNumber', { number: roomNumber })}
                </span>
              )}

              {/* Mobile heart + share pill */}
              <div
                className="flex md:hidden items-center rounded-full border border-white/30 overflow-hidden self-start"
                style={{ backgroundColor: 'rgba(113,113,113,0.45)', backdropFilter: 'blur(7px)' }}
              >
                <button
                  className={`px-3 py-1.5 flex items-center justify-center text-white transition-all duration-200 hover:bg-white/10 ${
                    isHeartLiked ? 'text-[#ff4757]' : ''
                  }`}
                  onClick={handleHeartClick}
                  aria-label={isHeartLiked ? t('unlike') : t('like')}
                >
                  {isHeartLiked ? <AiFillHeart size={18} /> : <AiOutlineHeart size={18} />}
                </button>
                <button
                  className="px-3 py-1.5 flex items-center justify-center text-white border-l border-white/30 transition-all duration-200 hover:bg-white/10"
                  onClick={handleShareClick}
                  aria-label={t('share')}
                >
                  <IoArrowRedoOutline size={18} />
                </button>
              </div>

              {/* Desktop actions pill */}
              <div className="hidden md:flex items-center h-12 rounded-full border border-white/30 bg-gray-500/45 backdrop-blur-[7px] overflow-hidden">
                <button
                  className={`h-full px-4 pl-5 flex items-center justify-center text-white transition-all duration-200 hover:bg-white/10 ${isHeartLiked ? 'text-red-500' : ''}`}
                  onClick={handleHeartClick}
                  aria-label={isHeartLiked ? t('unlike') : t('like')}
                  style={{ filter: 'drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.5))' }}
                >
                  {isHeartLiked ? <AiFillHeart size={24} /> : <AiOutlineHeart size={24} />}
                </button>
                <button
                  className="h-full px-3 flex items-center justify-center text-white border-l border-white/40 transition-all duration-200 hover:bg-white/10 hover:text-blue-300"
                  onClick={handleShareClick}
                  aria-label={t('share')}
                  style={{ filter: 'drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.5))' }}
                >
                  <IoArrowRedoOutline size={24} />
                </button>
                <button
                  onClick={() => document.getElementById('room-content')?.scrollIntoView({ behavior: 'smooth' })}
                  className="h-full px-4 pr-5 flex items-center gap-1.5 text-white text-xl font-medium font-inter border-l border-white/40 transition-all duration-200 hover:bg-white/10 group"
                  style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.54)' }}
                >
                  {t('seeMore')}
                  <IoChevronDown size={20} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom section - Price, Calendar, Booking */}
          {/* Default (tall viewports, mobile/md): stacked above the calendar, anchored to the right.
              Split layout only kicks in on `short:lg` (wide enough for two columns AND short enough
              that stacking would overflow vertically) — price/summary pins to bottom-LEFT, calendar
              cluster to bottom-RIGHT. Threshold defined by the `short` variant in globals.css. */}
          <div className="flex flex-col shrink-0 items-end self-end gap-2 md:gap-4 short:flex-row short:items-end short:gap-6 short:self-stretch short:justify-between">
            {showCalendar ? (
              <>
                {/* Left column: price (or selected-interval summary). Pinned to bottom-left only on short:lg+ */}
                <div className="flex flex-col items-end short:items-start">
                  {!hasCompleteInterval && (
                    <div className="flex flex-col items-end short:items-start">
                      <span
                        className="text-white text-lg md:text-2xl font-normal font-inter px-3 py-0.5 rounded-full backdrop-blur-[1px]"
                        style={{ textShadow: '1px 1px 6px black' }}
                      >
                        {t('startingFrom')}
                      </span>
                      <span
                        className="text-white text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold font-poppins leading-none -mb-0.5 backdrop-blur-[1px]"
                        style={{ textShadow: '2px 2px 18px rgba(0, 0, 0, 0.66)' }}
                      >
                        {formatPrice(minPrice)}
                      </span>
                      <span
                        className="text-white text-lg md:text-2xl font-normal font-inter px-3 py-0.5 rounded-full backdrop-blur-[1px]"
                        style={{ textShadow: '1px 1px 6px black' }}
                      >
                        {t('perNight')}
                      </span>
                    </div>
                  )}

                  {hasCompleteInterval && (
                    <BookingSummary
                      interval={interval}
                      calendarDays={allCalendarDays}
                      currency={currency}
                      onClear={handleClearSelection}
                    />
                  )}
                </div>

                {/* Right column on lg+: select-dates message, calendar, desktop booking buttons */}
                <div className="flex flex-col items-end gap-2 md:gap-4">
                  {showSelectDatesMsg && (
                    <div
                      className="text-center text-white text-sm font-medium animate-fade-in mb-1"
                      style={{ textShadow: '1px 1px 4px black' }}
                    >
                      {t('selectYourDates')}
                    </div>
                  )}

                  <div
                    ref={calendarRef}
                    className={`rounded-xl transition-all duration-500 ${
                      calendarHighlight
                        ? 'ring-3 ring-white/80 ring-offset-2 ring-offset-transparent shadow-[0_0_30px_rgba(255,255,255,0.4)]'
                        : ''
                    }`}
                  >
                    <AvailabilityCalendar
                      roomSlug={roomSlug}
                      initialData={initialCalendarData}
                      interval={interval}
                      onIntervalChange={handleIntervalChange}
                      onCalendarDataChange={handleCalendarDataChange}
                      onPricesLoaded={handlePricesLoaded}
                      onError={handleCalendarError}
                    />
                  </div>

                  <div className="hidden md:flex w-96 -mt-4">
                    <button
                      onClick={handleSelectThis}
                      className={`flex-1 py-3 px-2 text-lg font-semibold font-inter border border-r-0 rounded-bl-lg transition-all duration-200 cursor-pointer ${
                        isSelected
                          ? 'bg-white/20 backdrop-blur-[7px] border-white/60 text-white'
                          : 'bg-gray-500/45 backdrop-blur-[7px] border-white/45 text-white hover:bg-white/10 hover:border-white/50'
                      }`}
                    >
                      {isSelected ? `✓ ${t('selected')}` : t('selectThis')}
                    </button>
                    <button
                      onClick={handleBookNow}
                      className="flex-1 py-3 px-2 text-lg font-semibold font-inter uppercase rounded-br-lg transition-all duration-200 text-white bg-[#801515] hover:bg-[#601010] border border-l-0 border-white/45 cursor-pointer"
                    >
                      {t('bookNow')}{hasSelection ? ` (${items.length + (isSelected ? 0 : 1)})` : ''}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="text-white text-xl md:text-2xl font-inter text-right max-w-md backdrop-blur-[1px]"
                style={{ textShadow: '2px 2px 10px rgba(0, 0, 0, 0.7)' }}
              >
                {t('unavailableLine1')}
                {t('unavailableLine2') && <><br />{t('unavailableLine2')}</>}
                <br />
                <span className="text-white/80">{t('contactForAvailability')}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile sticky bottom button group (hidden when booking disabled) */}
      {!bookingDisabled && (
      <div
        className={`fixed left-0 right-0 md:hidden z-50 flex border-t border-black/20 transition-all duration-300 ${selectionBarVisible ? 'bottom-[48px]' : 'bottom-0'}`}
        style={{ paddingBottom: selectionBarVisible ? undefined : 'env(safe-area-inset-bottom, 0px)' }}
      >
        <button
          onClick={handleSelectThis}
          className={`flex-1 py-3 px-2 text-base font-semibold transition-all duration-200 cursor-pointer ${
            isSelected
              ? 'bg-[#3e2a20] text-white'
              : 'bg-[#4e3529] text-white/90 hover:text-white hover:bg-[#3e2a20]'
          }`}
        >
          {isSelected ? `✓ ${t('selected')}` : t('selectThis')}
        </button>
        <button
          onClick={handleBookNow}
          className="flex-1 py-3 px-2 text-base font-bold uppercase bg-[#801515] text-white hover:bg-[#601010] cursor-pointer transition-all duration-200"
          style={{ boxShadow: '0px 0px 17px -1px rgba(128,21,21,0.5)' }}
        >
          {t('bookNow')}{hasSelection ? ` (${items.length + (isSelected ? 0 : 1)})` : ''}
        </button>
      </div>
      )}

      {/* Keyframe animation for background pan */}
      <style jsx global>{`
        @keyframes slowHorizontalMove {
          0% { background-position-x: 10%; }
          50% { background-position-x: 90%; }
          100% { background-position-x: 10%; }
        }
      `}</style>
    </>
  );
}
